import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { TestBadge } from "@/components/TestBadge";
import { STATUS_META, ISSUE_SECTIONS, COMMS_STATUSES, DENIAL_REASONS } from "@/lib/constants";
import { FileUpload } from "@/components/FileUpload";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Loader2, Newspaper, Palette, Clock, MessageSquare, Lock, Send,
  CheckCircle2, XCircle, UserPlus, CalendarClock, Link2, Pencil, FileText, ExternalLink, History, Ban,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const DETAIL_FIELDS = {
  sunday_news: [
    ["category", "Category"], ["contact_person", "Contact person"], ["contact_info", "Contact info"],
    ["requested_publication_date", "Requested publication"], ["response_deadline", "Response deadline"],
    ["event_date", "Event date"], ["event_time", "Event time"], ["location", "Location"],
    ["attire", "Attire"], ["cost", "Cost"], ["registration_link", "Registration link"],
    ["registration_deadline", "Registration deadline"], ["call_to_action", "Call to action"],
  ],
  comms_request: [
    ["request_type", "Request type"], ["target_audience", "Target audience"], ["deliverables", "Deliverables"],
    ["event_info", "Event info"], ["event_date", "Event date"], ["desired_release_date", "Desired release"],
    ["final_deadline", "Final deadline"], ["estimated_completion", "Estimated completion"], ["priority", "Priority"],
    ["call_to_action", "Call to action"], ["printing_specs", "Printing specs"], ["budget", "Budget"],
  ],
};

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [team, setTeam] = useState([]);
  const [note, setNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null); // approved | approved_with_edits | denied
  const [instructions, setInstructions] = useState("");
  const [denialReason, setDenialReason] = useState("");
  const [leaderComment, setLeaderComment] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [proofFiles, setProofFiles] = useState([]);
  const [cancelReason, setCancelReason] = useState("");

  const isAdmin = user?.role === "comms_admin" || user?.role === "technology";
  const isComms = user?.role === "comms_admin" || user?.role === "technology" || user?.role === "comms_team" || user?.role === "pr_chairman";
  const isLeader = user?.role === "president" || user?.role === "vice_president";
  const token = localStorage.getItem("cc_token");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/submissions/${id}`);
      setSub(data);
      setNewStatus(data.status);
      setSchedDate(data.scheduled_date || "");
    } catch (e) {
      toast.error("Could not load submission");
      navigate("/submissions");
    }
  }, [id, navigate]);

  useEffect(() => { load(); if (user?.role === "comms_admin" || user?.role === "technology" || user?.role === "comms_team" || user?.role === "pr_chairman") api.get("/users/team").then((r) => setTeam(r.data)).catch(() => {}); }, [load, user]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!sub) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const d = sub.data || {};
  const title = d.title || d.project_purpose || "Untitled";
  const isNews = sub.submission_type === "sunday_news";
  const isOwner = sub.created_by === user?.user_id;
  const canEdit = isOwner && ["draft", "additional_info_required"].includes(sub.status);
  const NON_CANCELLABLE = ["published", "closed", "downloaded", "completed", "denied_by_leadership", "cancelled"];
  const canCancel = isOwner && !NON_CANCELLABLE.includes(sub.status);
  const images = d.images || [];
  const documents = [...(d.documents || []), ...(d.attachments || [])];
  const fileUrl = (f) => `${API}/files/${f.path}?auth=${token}`;

  const copy = isNews ? d.announcement_copy : d.final_wording;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${isNews ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
            {isNews ? <Newspaper className="h-6 w-6" /> : <Palette className="h-6 w-6" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="detail-title">{title}</h1>
              {sub.is_test && <TestBadge />}
            </div>
            <p className="text-sm text-muted-foreground">
              {isNews ? "Sunday News" : "Communications Request"} · {sub.committee || "—"} · by {sub.creator_name || "Member"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={sub.status} />
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" asChild data-testid="edit-submission-btn">
                <Link to={`/submit/${isNews ? "sunday-news" : "comms-request"}/${sub.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Link>
              </Button>
            )}
            {canCancel && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="cancel-request-btn"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                    <Ban className="mr-1.5 h-3.5 w-3.5" /> Cancel request
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="cancel-request-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel this request?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This withdraws the request from the workflow. It will be marked <strong>Cancelled</strong> and the Communications team will be notified. You can't undo this — you'd need to submit a new request.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Reason (optional)</Label>
                    <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Let the team know why you're cancelling (optional)…" data-testid="cancel-reason-input" />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-request-dismiss">Keep request</AlertDialogCancel>
                    <AlertDialogAction data-testid="cancel-request-confirm"
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => act(() => api.post(`/submissions/${id}/cancel`, { reason: cancelReason }).then(() => setCancelReason("")), "Request cancelled")}>
                      Cancel request
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {sub.leadership_instructions && (
            <Card className="border-accent/50 bg-accent/5 p-6" data-testid="leadership-instructions-banner">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-accent">
                <Pencil className="h-5 w-5" /> Leadership Edits & Instructions
              </h2>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{sub.leadership_instructions}</p>
              {(sub.leadership_edit_files || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {sub.leadership_edit_files.map((f) => (
                    <a key={f.id} href={fileUrl(f)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 rounded-lg border bg-card p-2 text-sm hover:bg-secondary" data-testid={`leader-edit-file-${f.id}`}>
                      <FileText className="h-4 w-4 text-accent" /> <span className="flex-1 truncate">{f.filename}</span>
                    </a>
                  ))}
                </div>
              )}
            </Card>
          )}
          {sub.status === "denied_by_leadership" && (
            <Card className="border-destructive/40 bg-destructive/5 p-6" data-testid="denial-banner">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-destructive">
                <XCircle className="h-5 w-5" /> Denied by Leadership
              </h2>
              {sub.denial_reason && <p className="mt-2 text-sm">{sub.denial_reason}</p>}
            </Card>
          )}
          {copy && (
            <Card className="p-6">
              <h2 className="font-heading text-lg font-semibold">{isNews ? "Announcement copy" : "Approved wording"}</h2>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed" data-testid="detail-copy">{copy}</p>
            </Card>
          )}

          <Card className="p-6">
            <h2 className="font-heading text-lg font-semibold">Details</h2>
            <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {(DETAIL_FIELDS[sub.submission_type] || []).map(([k, label]) =>
                d[k] ? (
                  <div key={k}>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
                    <dd className="text-sm font-medium break-words">
                      {typeof d[k] === "string" && d[k].startsWith("http") ? (
                        <a href={d[k]} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Link <ExternalLink className="h-3 w-3" /></a>
                      ) : String(d[k])}
                    </dd>
                  </div>
                ) : null
              )}
              {d.distribution_channels?.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Distribution channels</dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">{d.distribution_channels.map((c) => <span key={c} className="rounded-full bg-secondary px-2 py-0.5 text-xs">{c}</span>)}</dd>
                </div>
              )}
            </dl>
            {sub.linked_submission_id && (
              <Link to={`/submissions/${sub.linked_submission_id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="linked-submission">
                <Link2 className="h-4 w-4" /> View connected submission
              </Link>
            )}
          </Card>

          {images.length > 0 && (
            <Card className="p-6">
              <h2 className="font-heading text-lg font-semibold">Images</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {images.map((f) => (
                  <a key={f.id} href={fileUrl(f)} target="_blank" rel="noreferrer" data-testid={`image-${f.id}`}>
                    <img src={fileUrl(f)} alt={f.filename} className="aspect-square w-full rounded-lg border object-cover transition-transform hover:-translate-y-1" />
                  </a>
                ))}
              </div>
            </Card>
          )}

          {documents.length > 0 && (
            <Card className="p-6">
              <h2 className="font-heading text-lg font-semibold">Documents</h2>
              <div className="mt-3 space-y-2">
                {documents.map((f) => (
                  <a key={f.id} href={fileUrl(f)} target="_blank" rel="noreferrer" data-testid={`doc-${f.id}`}
                    className="flex items-center gap-2 rounded-lg border p-3 text-sm transition-colors hover:bg-secondary">
                    <FileText className="h-4 w-4 text-primary" /> <span className="flex-1 truncate">{f.filename}</span> <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </Card>
          )}

          {/* Notes / comments */}
          <Card className="p-6">
            <Tabs defaultValue="public">
              <TabsList>
                <TabsTrigger value="public" data-testid="tab-public-notes"><MessageSquare className="mr-1.5 h-4 w-4" /> Comments</TabsTrigger>
                {isComms && <TabsTrigger value="internal" data-testid="tab-internal-notes"><Lock className="mr-1.5 h-4 w-4" /> Internal notes</TabsTrigger>}
              </TabsList>
              <TabsContent value="public" className="space-y-3">
                {(sub.public_notes || []).length === 0 && <p className="py-4 text-sm text-muted-foreground">No comments yet.</p>}
                {(sub.public_notes || []).map((n, i) => (
                  <div key={`${n.at || "note"}-${i}`} className="rounded-lg border bg-secondary/40 p-3" data-testid={`public-note-${i}`}>
                    <p className="text-sm">{n.text}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{n.by} · {n.at ? format(new Date(n.at), "MMM d, h:mm a") : ""}</p>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a comment…" rows={2} data-testid="public-note-input" />
                  <Button disabled={busy || !note.trim()} data-testid="add-public-note-btn"
                    onClick={() => act(() => api.post(`/submissions/${id}/notes`, { text: note, internal: false }).then(() => setNote("")), "Comment added")}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>
              {isComms && (
                <TabsContent value="internal" className="space-y-3">
                  {(sub.internal_notes || []).length === 0 && <p className="py-4 text-sm text-muted-foreground">No internal notes.</p>}
                  {(sub.internal_notes || []).map((n, i) => (
                    <div key={`${n.at || "note"}-${i}`} className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`internal-note-${i}`}>
                      <p className="text-sm text-amber-900">{n.text}</p>
                      <p className="mt-1 text-xs text-amber-700">{n.by} · {n.at ? format(new Date(n.at), "MMM d, h:mm a") : ""}</p>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} placeholder="Internal note (team only)…" rows={2} data-testid="internal-note-input" />
                    <Button disabled={busy || !internalNote.trim()} data-testid="add-internal-note-btn"
                      onClick={() => act(() => api.post(`/submissions/${id}/notes`, { text: internalNote, internal: true }).then(() => setInternalNote("")), "Internal note added")}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Requestor proof approval */}
          {isOwner && sub.status === "awaiting_requestor_approval" && (
            <Card className="space-y-3 p-5" data-testid="proof-approval-panel">
              <h3 className="font-heading font-semibold">Approve your proof</h3>
              <p className="text-xs text-muted-foreground">Communications prepared a proof. Review it below, then approve or request changes.</p>
              {(sub.proof_files || []).length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-border bg-secondary/40 p-3" data-testid="proof-files">
                  <p className="text-xs font-semibold text-muted-foreground">Proof files</p>
                  {sub.proof_files.map((f, i) => (
                    <a key={i} href={`${API}/files/${f.path}?auth=${token}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid={`proof-file-${i}`}>
                      <FileText className="h-3.5 w-3.5" /> {f.name || "Proof file"}
                    </a>
                  ))}
                </div>
              )}
              <Textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Optional note to Communications…" data-testid="proof-note-input" />
              <div className="grid grid-cols-1 gap-2">
                <Button size="sm" className="justify-start bg-green-700 text-white hover:bg-green-800" disabled={busy} data-testid="proof-approve-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/proof-decision`, { decision: "approved", note: statusNote }).then(() => setStatusNote("")), "Proof approved")}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve proof
                </Button>
                <Button size="sm" variant="outline" disabled={busy} data-testid="proof-changes-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/proof-decision`, { decision: "changes", note: statusNote }).then(() => setStatusNote("")), "Changes requested")}>
                  <Pencil className="mr-1.5 h-4 w-4" /> Request changes
                </Button>
              </div>
            </Card>
          )}
          {/* Communications Chairman review (initial triage) */}
          {isAdmin && sub.status === "comms_review" && (
            <Card className="space-y-3 p-5" data-testid="comms-review-panel">
              <h3 className="font-heading font-semibold">Communications Review</h3>
              <p className="text-xs text-muted-foreground">Review this new request. Accept to send it to leadership to approve production, or request more information from the requestor.</p>
              <Textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Optional note…" data-testid="comms-review-note" />
              <div className="grid grid-cols-1 gap-2">
                <Button size="sm" className="justify-start" disabled={busy} data-testid="comms-accept-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/comms-review`, { decision: "accept", note: statusNote }).then(() => setStatusNote("")), "Sent to leadership")}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Accept & send to leadership
                </Button>
                <Button size="sm" variant="outline" className="justify-start" disabled={busy} data-testid="comms-needsinfo-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/comms-review`, { decision: "needs_info", note: statusNote }).then(() => setStatusNote("")), "Sent back for more info")}>
                  <Pencil className="mr-1.5 h-4 w-4" /> Request more info
                </Button>
              </div>
            </Card>
          )}
          {/* Leadership decision */}
          {isLeader && (sub.status === "awaiting_leadership_approval" || sub.status === "final_leadership_review") && (
            <Card className="space-y-3 p-5" data-testid="leadership-decision-panel">
              <h3 className="font-heading font-semibold">{sub.status === "final_leadership_review" ? "Final Approval" : "Leadership Decision"}</h3>
              <p className="text-xs text-muted-foreground">{sub.status === "final_leadership_review" ? "The requestor approved the proof. Give final approval before it is published/delivered." : `You are reviewing as ${user?.name}. Choose a decision below.`}</p>
              <div className="grid grid-cols-1 gap-2">
                <Button variant={decision === "approved" ? "default" : "outline"} size="sm" data-testid="decision-approve"
                  onClick={() => setDecision("approved")} className="justify-start">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve
                </Button>
                <Button variant={decision === "approved_with_edits" ? "default" : "outline"} size="sm" data-testid="decision-approve-edits"
                  onClick={() => setDecision("approved_with_edits")} className="justify-start">
                  <Pencil className="mr-1.5 h-4 w-4" /> Approve With Edits
                </Button>
                <Button variant={decision === "denied" ? "destructive" : "outline"} size="sm" data-testid="decision-deny"
                  onClick={() => setDecision("denied")} className="justify-start">
                  <XCircle className="mr-1.5 h-4 w-4" /> Deny Request
                </Button>
              </div>

              {decision === "approved_with_edits" && (
                <div className="space-y-2 rounded-lg border border-accent/40 bg-accent/5 p-3">
                  <Label className="text-xs">Leadership Edits and Instructions <span className="text-accent">*</span></Label>
                  <Textarea rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Revised wording, corrections, content to add/remove, date changes, guidance…" data-testid="leader-instructions-input" />
                  <Label className="text-xs">Upload edited document (optional)</Label>
                  <FileUpload testId="leader-edit-files" files={editFiles} onChange={setEditFiles} />
                </div>
              )}

              {decision === "denied" && (
                <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <Label className="text-xs">Reason for Denial</Label>
                  <Select value={denialReason} onValueChange={setDenialReason}>
                    <SelectTrigger data-testid="denial-reason-select"><SelectValue placeholder="Select a reason (optional)" /></SelectTrigger>
                    <SelectContent>{DENIAL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea rows={2} value={denialReason && !DENIAL_REASONS.includes(denialReason) ? denialReason : ""}
                    onChange={(e) => setDenialReason(e.target.value)} placeholder="Or type a custom explanation…" data-testid="denial-reason-input" />
                </div>
              )}

              {decision && decision !== "denied" && (
                <Textarea rows={2} value={leaderComment} onChange={(e) => setLeaderComment(e.target.value)}
                  placeholder="Optional leadership comment for the requestor" data-testid="leader-comment-input" />
              )}

              {decision && (
                <Button className="w-full" disabled={busy || (decision === "approved_with_edits" && !instructions.trim())}
                  variant={decision === "denied" ? "destructive" : "default"} data-testid="confirm-decision-btn"
                  onClick={() => {
                    if (!window.confirm("Confirm this leadership decision? It will move the request forward.")) return;
                    act(() => api.post(`/submissions/${id}/leadership-decision`, {
                      decision, instructions, reason: denialReason, comment: leaderComment, files: editFiles,
                    }).then(() => { setDecision(null); setInstructions(""); setDenialReason(""); setLeaderComment(""); setEditFiles([]); }),
                    "Decision recorded");
                  }}>
                  Confirm decision
                </Button>
              )}
            </Card>
          )}

          {/* Leadership reopen */}
          {isLeader && sub.status === "denied_by_leadership" && (
            <Card className="space-y-2 p-5" data-testid="reopen-panel">
              <h3 className="font-heading font-semibold">Reopen request</h3>
              <p className="text-xs text-muted-foreground">Reopen this denied request for another leadership review.</p>
              <Button className="w-full" size="sm" variant="outline" disabled={busy} data-testid="reopen-btn"
                onClick={() => act(() => api.post(`/submissions/${id}/reopen`, {}), "Request reopened")}>
                Reopen for review
              </Button>
            </Card>
          )}

          {/* Recorded leadership decisions (visibility) */}
          {(sub.leadership_approvals || []).length > 0 && (
            <Card className="p-5" data-testid="leadership-record">
              <h3 className="font-heading font-semibold">Leadership record</h3>
              <div className="mt-3 space-y-2">
                {sub.leadership_approvals.map((a, i) => (
                  <div key={`${a.role || a.name || "approval"}-${i}`} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                    <span>{a.name}</span>
                    <span className="text-xs font-semibold capitalize text-muted-foreground">{a.decision.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Communications actions */}
          {isComms && !["comms_review", "awaiting_leadership_approval", "final_leadership_review", "denied_by_leadership", "draft"].includes(sub.status) && (
            <Card className="space-y-4 p-5" data-testid="admin-actions-panel">
              <h3 className="font-heading font-semibold">Production actions</h3>
              <div>
                <Label className="text-xs">Change status</Label>
                <Select value={COMMS_STATUSES.includes(newStatus) ? newStatus : ""} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>{COMMS_STATUSES.map((k) => <SelectItem key={k} value={k}>{STATUS_META[k]?.label || k}</SelectItem>)}</SelectContent>
                </Select>
                {newStatus === "awaiting_requestor_approval" && (
                  <div className="mt-2 rounded-lg border border-accent/40 bg-accent/5 p-3">
                    <Label className="text-xs">Attach proof file(s) for the requestor</Label>
                    <FileUpload testId="proof-files-upload" files={proofFiles} onChange={setProofFiles} />
                  </div>
                )}
                <Textarea className="mt-2" rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Note to member (optional)" data-testid="status-note-input" />
                <Button className="mt-2 w-full" size="sm" disabled={busy || !newStatus} data-testid="update-status-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/status`, { status: newStatus, note: statusNote, proof_files: newStatus === "awaiting_requestor_approval" ? proofFiles : null }).then(() => { setStatusNote(""); setProofFiles([]); }), "Status updated")}>
                  Update status
                </Button>
              </div>
              <div className="border-t pt-4">
                <Label className="text-xs flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Assign to</Label>
                <Select value={sub.assigned_to || ""} onValueChange={(v) => act(() => api.post(`/submissions/${id}/assign`, { assigned_to: v }), "Assigned")}>
                  <SelectTrigger className="mt-1.5" data-testid="assign-select"><SelectValue placeholder="Choose team member" /></SelectTrigger>
                  <SelectContent>{team.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                {sub.assigned_name && <p className="mt-1.5 text-xs text-muted-foreground">Assigned to {sub.assigned_name}</p>}
              </div>
              <div className="border-t pt-4">
                <Label className="text-xs flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Schedule</Label>
                <Input type="date" className="mt-1.5" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="schedule-date-input" />
                <Button className="mt-2 w-full" size="sm" variant="outline" disabled={busy || !schedDate} data-testid="schedule-btn"
                  onClick={() => act(() => api.post(`/submissions/${id}/schedule`, { scheduled_date: schedDate }), "Scheduled")}>
                  Schedule
                </Button>
              </div>
              {sub.submission_type === "comms_request" && d.include_in_sunday_news && !sub.linked_submission_id && (
                <div className="border-t pt-4">
                  <Button className="w-full" size="sm" variant="secondary" disabled={busy} data-testid="create-connected-btn"
                    onClick={() => act(() => api.post(`/submissions/${id}/create-sunday-news`), "Connected Sunday News item created")}>
                    <Link2 className="mr-1.5 h-4 w-4" /> Create Sunday News item
                  </Button>
                </div>
              )}
            </Card>
          )}

          <Card className="p-5">
            <h3 className="font-heading font-semibold flex items-center gap-1.5"><History className="h-4 w-4" /> Activity</h3>
            <div className="mt-4 space-y-4" data-testid="activity-timeline">
              {[...(sub.history || [])].reverse().map((h, i) => (
                <div key={`${h.at || "hist"}-${i}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    {i < sub.history.length - 1 && <div className="mt-1 h-full w-px flex-1 bg-border" />}
                  </div>
                  <div className="-mt-1 pb-1">
                    <p className="text-sm font-medium capitalize">{h.action}</p>
                    {h.note && <p className="text-xs text-muted-foreground">"{h.note}"</p>}
                    <p className="text-xs text-muted-foreground">{h.by} · {h.at ? format(new Date(h.at), "MMM d, h:mm a") : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
